import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { toast } from "sonner";
import { 
  Upload, FileText, Activity, ArrowLeft, Trash2, Plus, 
  Sparkles, FlaskConical, Layers, TrendingUp, TrendingDown, AlertCircle,
  BarChart3, ArrowRightLeft, CheckCircle2, XCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, ReferenceLine
} from "recharts";
import SEO from "@/components/SEO";
import ReproducibleAIBadge from "@/components/ReproducibleAIBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ANALYSIS_TYPES = [
  { value: "ftir-spectrum", label: "FTIR Spectrum" },
];

const COLORS = ["#2563eb", "#dc2626", "#16a34a", "#9333ea", "#ea580c"];

const CompareSpectraPage = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [analysisType, setAnalysisType] = useState("ftir-spectrum");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (files.length + acceptedFiles.length > 5) {
      toast.error("Maximum 5 files allowed for comparison");
      return;
    }

    for (const file of acceptedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/demo/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        setFiles(prev => [...prev, {
          id: response.data.file_id,
          name: file.name,
          label: `Sample ${prev.length + 1}`,
          color: COLORS[prev.length % COLORS.length]
        }]);

        toast.success(`${file.name} uploaded`);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
  }, [files.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles: 5
  });

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const updateLabel = (index, label) => {
    setFiles(prev => prev.map((f, i) => i === index ? { ...f, label } : f));
  };

  const runComparison = async () => {
    if (files.length < 2) {
      toast.error("Please upload at least 2 files to compare");
      return;
    }

    setIsAnalyzing(true);
    try {
      const response = await axios.post(`${API}/demo/compare`, {
        file_ids: files.map(f => f.id),
        analysis_type: analysisType,
        labels: files.map(f => f.label)
      });

      setComparisonResult(response.data);
      toast.success("Comparison complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Comparison failed");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Prepare overlay chart data
  const prepareChartData = () => {
    if (!comparisonResult?.spectra_data) return [];
    
    const firstSpectrum = comparisonResult.spectra_data[0];
    const chartData = firstSpectrum.x.map((x, i) => {
      const point = { x };
      comparisonResult.spectra_data.forEach((spectrum, j) => {
        point[`y${j}`] = spectrum.y[i];
      });
      return point;
    });
    
    return chartData;
  };

  const chartData = prepareChartData();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <SEO 
        title="Compare Spectra - TabedLab"
        description="Compare multiple FTIR spectra side by side with AI-powered analysis"
      />

      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-xl border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/demo')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Demo
              </Button>
              <div className="h-6 w-px bg-slate-700"></div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Layers className="w-5 h-5 text-cyan-400" />
                Compare Spectra
              </h1>
            </div>
            <ReproducibleAIBadge variant="compact" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {!comparisonResult ? (
          /* Upload Section */
          <div className="space-y-6">
            {/* Instructions */}
            <Card className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-cyan-500/30 p-6">
              <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-cyan-400" />
                Multi-Spectra Comparison
              </h2>
              <p className="text-slate-400">
                Upload 2-5 spectra files (CSV) to compare. Label each spectrum (e.g., "Pure Sample", "Modified Sample") 
                for clear identification. The AI will detect peak shifts, intensity changes, and new/disappearing peaks.
              </p>
            </Card>

            {/* Analysis Type Selection */}
            <div className="flex items-center gap-4">
              <label className="text-slate-400">Analysis Type:</label>
              <Select value={analysisType} onValueChange={setAnalysisType}>
                <SelectTrigger className="w-64 bg-slate-800 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  {ANALYSIS_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dropzone */}
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer ${
                isDragActive 
                  ? 'border-cyan-400 bg-cyan-500/10' 
                  : 'border-slate-600 hover:border-cyan-500/50 hover:bg-slate-800/50'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragActive ? 'text-cyan-400' : 'text-slate-500'}`} />
              <p className="text-slate-300 text-lg mb-2">
                {isDragActive ? 'Drop files here...' : 'Drag & drop spectrum files'}
              </p>
              <p className="text-slate-500 text-sm">
                CSV or Excel files (max 5 files)
              </p>
            </div>

            {/* Uploaded Files */}
            {files.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-slate-300 font-medium">Uploaded Spectra ({files.length}/5)</h3>
                {files.map((file, index) => (
                  <div 
                    key={file.id}
                    className="flex items-center gap-4 p-4 bg-slate-800/50 border border-slate-700 rounded-lg"
                  >
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: file.color }}
                    />
                    <FileText className="w-5 h-5 text-slate-400" />
                    <span className="text-slate-300 flex-1">{file.name}</span>
                    <Input
                      value={file.label}
                      onChange={(e) => updateLabel(index, e.target.value)}
                      placeholder="Label (e.g., Pure Sample)"
                      className="w-48 bg-slate-700 border-slate-600 text-white text-sm"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(index)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Compare Button */}
            <Button
              onClick={runComparison}
              disabled={files.length < 2 || isAnalyzing}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white py-6 text-lg"
              data-testid="compare-button"
            >
              {isAnalyzing ? (
                <>
                  <Activity className="w-5 h-5 mr-2 animate-pulse" />
                  Comparing Spectra...
                </>
              ) : (
                <>
                  <ArrowRightLeft className="w-5 h-5 mr-2" />
                  Compare {files.length} Spectra
                </>
              )}
            </Button>
          </div>
        ) : (
          /* Results Section */
          <div className="space-y-8">
            {/* Back to Upload */}
            <Button
              variant="ghost"
              onClick={() => setComparisonResult(null)}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              New Comparison
            </Button>

            {/* Comparison Summary */}
            <Card className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-cyan-500/30 p-6">
              <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-cyan-400" />
                Comparison Summary
              </h2>
              <p className="text-slate-300 leading-relaxed">
                {comparisonResult.comparison?.comparison_summary || "Comparative analysis complete."}
              </p>
            </Card>

            {/* Overlay Chart */}
            <Card className="bg-white p-6 border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Layers className="w-5 h-5 text-blue-500" />
                Spectra Overlay
              </h3>
              
              {/* Legend */}
              <div className="flex flex-wrap gap-4 mb-4">
                {comparisonResult.spectra_data?.map((spectrum, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: spectrum.color }}
                    />
                    <span className="text-sm text-slate-600">{spectrum.label}</span>
                    <span className="text-xs text-slate-400">({spectrum.peaks?.length || 0} peaks)</span>
                  </div>
                ))}
              </div>

              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData} margin={{ top: 20, right: 30, left: 60, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="x"
                    stroke="#374151"
                    tick={{ fill: '#374151', fontSize: 11 }}
                    label={{ 
                      value: comparisonResult.chart_config?.xlabel || 'Wavenumber (cm⁻¹)', 
                      position: 'insideBottom', 
                      offset: -15,
                      fill: '#1f2937',
                      fontSize: 13
                    }}
                    reversed={comparisonResult.chart_config?.reversed_x}
                  />
                  <YAxis
                    stroke="#374151"
                    tick={{ fill: '#374151', fontSize: 11 }}
                    label={{ 
                      value: comparisonResult.chart_config?.ylabel || 'Transmittance (%)', 
                      angle: -90, 
                      position: 'insideLeft',
                      fill: '#1f2937',
                      fontSize: 13,
                      dx: -10
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                    }}
                  />
                  <Legend />
                  {comparisonResult.spectra_data?.map((spectrum, i) => (
                    <Line
                      key={i}
                      type="monotone"
                      dataKey={`y${i}`}
                      name={spectrum.label}
                      stroke={spectrum.color}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                  {/* Peak markers for reference spectrum */}
                  {comparisonResult.spectra_data?.[0]?.peaks?.map((peak, i) => (
                    <ReferenceLine
                      key={`peak-${i}`}
                      x={peak.x}
                      stroke="#94a3b8"
                      strokeDasharray="4 4"
                      strokeWidth={1}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>

              {/* Detected Peaks with Scientific Explanations */}
              <div className="mt-6 pt-4 border-t border-slate-200">
                <h4 className="font-medium text-slate-700 mb-3">Detected Peaks (Reference: {comparisonResult.spectra_data?.[0]?.label})</h4>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {comparisonResult.spectra_data?.[0]?.peaks?.map((peak, i) => (
                    <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-mono font-medium text-blue-600">
                          {peak.x?.toFixed(0)} cm⁻¹
                        </span>
                        <span className="text-xs text-slate-500">
                          {peak.intensity?.toFixed(1)}% abs
                        </span>
                      </div>
                      {peak.area_percent && (
                        <p className="text-xs text-slate-500">Area: {peak.area_percent}%</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            {/* Chemical Changes Detected */}
            {comparisonResult.comparison?.chemical_changes_detected?.length > 0 && (
              <Card className="bg-slate-800/50 border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-amber-400 mb-4 flex items-center gap-2">
                  <FlaskConical className="w-5 h-5" />
                  Chemical Changes Detected
                </h3>
                <div className="space-y-4">
                  {comparisonResult.comparison.chemical_changes_detected.map((change, i) => (
                    <div key={i} className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-amber-300">{change.change_type}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          change.confidence?.toLowerCase().includes('high') 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {change.confidence}
                        </span>
                      </div>
                      <p className="text-slate-300 text-sm mb-2">{change.evidence}</p>
                      <p className="text-slate-400 text-xs italic">{change.scientific_explanation}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Peak Shifts */}
            {comparisonResult.comparison?.peak_shifts?.length > 0 && (
              <Card className="bg-slate-800/50 border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-blue-400 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Peak Shifts Detected
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-700/50">
                        <th className="px-4 py-2 text-left text-slate-400">Reference</th>
                        <th className="px-4 py-2 text-left text-slate-400">Shifted To</th>
                        <th className="px-4 py-2 text-left text-slate-400">Shift</th>
                        <th className="px-4 py-2 text-left text-slate-400">Direction</th>
                        <th className="px-4 py-2 text-left text-slate-400">Sample</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonResult.comparison.peak_shifts.map((shift, i) => (
                        <tr key={i} className="border-b border-slate-700/50">
                          <td className="px-4 py-2 text-cyan-400 font-mono">
                            {shift.reference_position?.toFixed(0)} cm⁻¹
                          </td>
                          <td className="px-4 py-2 text-white font-mono">
                            {shift.shifted_position?.toFixed(0)} cm⁻¹
                          </td>
                          <td className="px-4 py-2 text-slate-300">
                            {shift.shift > 0 ? '+' : ''}{shift.shift?.toFixed(1)} cm⁻¹
                          </td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              shift.direction === 'blue-shift' 
                                ? 'bg-blue-500/20 text-blue-400' 
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {shift.direction}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-slate-300">{shift.sample}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* New Peaks */}
            {comparisonResult.comparison?.new_peaks?.length > 0 && (
              <Card className="bg-slate-800/50 border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-green-400 mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  New Peaks Detected
                </h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {comparisonResult.comparison.new_peaks.map((peak, i) => (
                    <div key={i} className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Plus className="w-4 h-4 text-green-400" />
                        <span className="font-mono text-green-400">{peak.position?.toFixed(0)} cm⁻¹</span>
                        <span className="text-xs text-slate-400">in {peak.sample}</span>
                      </div>
                      <p className="text-xs text-slate-400">{peak.significance}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Disappearing Peaks */}
            {comparisonResult.comparison?.disappearing_peaks?.length > 0 && (
              <Card className="bg-slate-800/50 border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-red-400 mb-4 flex items-center gap-2">
                  <XCircle className="w-5 h-5" />
                  Disappearing Peaks
                </h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {comparisonResult.comparison.disappearing_peaks.map((peak, i) => (
                    <div key={i} className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Trash2 className="w-4 h-4 text-red-400" />
                        <span className="font-mono text-red-400">{peak.position?.toFixed(0)} cm⁻¹</span>
                        <span className="text-xs text-slate-400">missing in {peak.sample}</span>
                      </div>
                      <p className="text-xs text-slate-400">{peak.significance}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Publication Paragraph */}
            {comparisonResult.comparison?.publication_comparison_paragraph && (
              <Card className="bg-slate-800/50 border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-green-400 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Publication-Ready Comparison Paragraph
                </h3>
                <div className="p-4 bg-slate-900/50 border border-green-500/30 rounded-lg">
                  <p className="text-slate-300 leading-relaxed italic">
                    "{comparisonResult.comparison.publication_comparison_paragraph}"
                  </p>
                </div>
              </Card>
            )}

            {/* CTA */}
            <Card className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-cyan-500/30 p-8 text-center">
              <h3 className="text-xl font-bold text-white mb-3">
                Unlock Full Comparison Features
              </h3>
              <p className="text-slate-400 mb-6">
                Get unlimited comparisons, PDF reports, and advanced quantitative analysis.
              </p>
              <div className="flex justify-center gap-4">
                <Button
                  onClick={() => navigate('/register')}
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-6 text-lg"
                >
                  Create Free Account
                </Button>
              </div>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default CompareSpectraPage;
